import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import lodash from "lodash";
import DataTable from "../global/DataTable";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import CustomSelect, { optionType } from "../organism/CustomSelect";
import PatientSearch from "../global/PatientSearch";
import dataConstants from "../../constants/dataConstants";
import reducers from "../../services/reducers";
import { PAGINATION_ACTIONS } from "../../services/reducers/PaginationReducer";
import CustomMultiSelect from "../organism/CustomMultiSelect";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import TableLoadingIndicator from "../global/LoadingIndicators/TableLoadingIndicator";
import OrderDetailResponse from "../../services/models/API/responseModels/onlineOrderResponseModels/orderDetailResponse.model";
import Button from "../organism/Button";
import useProfile from "../../services/utils/custom hooks/useProfile";
import { useLocation } from "react-router-dom";
import CustomText from "../atomic/CustomText";
import useActiveLabelsList from "../../services/utils/custom hooks/useActiveLabelsList";
import { selectedRowsState } from "../../services/context/selectedRowsIDContext";
import CustomTextInput from "../global/CustomTextInput";
import ContextFilters from "../global/ContextFiltersForTables";
import InfoSliderDispatcher from "../molecular/InfoSliderDispatcherButton";
import ErrorBoundary from "../global/ErrorBoundary";


function ActiveLabelsTable({
  tableContainerClass,
  selectedRowIds,
}: {
  tableContainerClass?: string;
  selectedRowIds?: number[];
}): JSX.Element {
  const location = useLocation();
  const [inputText, setInputText] = useState<string>("");
  const [queryText, setQueryText] = useState<string>("");
  const {
    state: { selectedOrganization, selectedPhysician },
    dispatch: applicationStateDispatcher,
  } = useContext(ApplicationState);
  const [params, setParams] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data, isLoading, isError } = useActiveLabelsList({
    params,
    updatePagination: ({ totalPages }) => {
      dispatch({ type: "setPageCount", payload: totalPages });
    },
    sort: sorting?.[0],
  });
  const [orders, setOrders] = useState([]);
  const query = useQueryParams();
  const queryPagination = {
    page: query.get("page"),
    size: query.get("size"),
  };
  const [{ pageIndex, pageSize, pageCount }, dispatch]: any = useReducer<any>(
    reducers.paginationReducer,
    {
      pageIndex: queryPagination?.page ? Number(queryPagination.page) : 1,
      pageSize: queryPagination?.size ? Number(queryPagination?.size) : 15,
      pageCount: data?.data?.totalPages ? data.data.totalPages : -1,
    }
  );
  const columns = useMemo(() => dataConstants.activeLabelTableColumns, []);
  const updatedColumns = columns.map((column) => {
    if (
      location?.pathname === "/access/shipments/unshipped-orders" &&
      column.header === "View Orders"
    ) {
      return { ...column, header: "Actions" };
    }
    return column;
  });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    updatedColumns
      .map((column: any) => ({
        id: column.accessorKey,
        value: query.get(column.accessorKey),
      }))
      .filter((data) => !!data.value)
  );

  const foundTestTypeFilterValue = columnFilters.find(
    (data) => data.id === "testTypeCode"
  )?.value;
  const pagination = {
    pageIndex,
    pageSize,
    pageCount,
    onNextPage,
    totalItems: data?.data?.totalItems ?? 0,
    onPreviousPage,
    setPage,
    setPageSize,
  };

  const debounceSearch = useCallback(lodash.debounce(setQueryText, 700), []);

  function onNextPage() {
    dispatch({ type: PAGINATION_ACTIONS.INCREMENT_PAGE });
  }

  function handleSearch(value: string) {
    setInputText(value);
    debounceSearch(value);
  }

  function setPage(page: number) {
    dispatch({ type: PAGINATION_ACTIONS.SET_PAGE, payload: page });
  }

  function setPageSize(size: number) {
    dispatch({ type: PAGINATION_ACTIONS.UPDATE_PAGE_SIZE, payload: size });
  }

  function onPreviousPage() {
    dispatch({ type: PAGINATION_ACTIONS.DECREMENT_PAGE });
  }

  function handleTestTypeFilter(option: optionType) {
    setColumnFilters((old) => {
      let temp = [...old];
      const foundIndex = temp.findIndex(
        (data: any) => data.id === "testTypeCode"
      );

      if (foundIndex !== -1) {
        if (temp[foundIndex]?.value !== option?.value) {
          if (option.value === "") {
            temp = temp.filter((data, index) => index !== foundIndex);
          } else
            temp[foundIndex] = { ...temp[foundIndex], value: option.value };
        } else {
          temp = temp.filter((data, index) => index !== foundIndex);
        }
      } else {
        temp.push({ id: "testTypeCode", value: option.value });
      }

      return temp;
    });
  }

  useEffect(() => {
    if (!isLoading) {
      setOrders(data?.data?.fedexLabels ?? []);
    }
  }, [isLoading, data]);

  if (isLoading && orders.length === 0) {
    return <TableLoadingIndicator />;
  }

  return (
    <ErrorBoundary>
      <div className="px-3 md:px-5">
        <div className="flex justify-start items-center pb-3">
          <CustomText
            text="Active Labels"
            fontWeight="font-semibold"
            fontSize="text-md"
            className=""
          />
          <InfoSliderDispatcher
            infoQuery={dataConstants.infoSliderQuery.activeShipments}
          />
        </div>

        <DataTable
          tableContainerClass={`2xl:h-[55vh] xl:h-[45vh] lg:h-[40vh] md:h-[30vh] overflow-auto border relative ${tableContainerClass}`}
          globalFilters={{
            organizationId: selectedOrganization,
          }}
          pagination={pagination}
          data={orders}
          setParams={setParams}
          isFetching={isLoading && orders.length > 0}
          columns={updatedColumns}
          enableClientSideFiltering={false}
          enableClientSideSorting={false}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          // rowClickable={true}
          columnVisibility={{
            id: false,
            size: false,
            page: false,
            testTypeCode: false,
          }}
          manualSorting={{ sorting, setSorting }}
        />
      </div>
    </ErrorBoundary>
  );
}

export default ActiveLabelsTable;
